/**
 * ClassificationAgentContext
 *
 * Global context for the Classification Agent chat panel.
 * Allows any component to open the agent with item context.
 *
 * Usage:
 *   const { openAgent } = useClassificationAgent();
 *   openAgent({ itemId: 123, description: '...', supplier: '...' });
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const ClassificationAgentContext = createContext({
  isOpen: false,
  context: null,
  openAgent: () => {},
  closeAgent: () => {},
});

export function useClassificationAgent() {
  return useContext(ClassificationAgentContext);
}

/**
 * Context shape that can be passed to openAgent:
 * {
 *   // Required - at least one of these
 *   itemId: number,           // procurement_raw_data.id
 *
 *   // Optional context (auto-fetched if not provided)
 *   description: string,      // item_description
 *   supplier: string,         // supplier_name
 *   amount: number,           // line_total
 *   componentName: string,    // current component
 *   componentId: number,      // tech_component_id
 *   confidence: number,       // component_confidence
 *   method: string,           // classification_method
 *   documentNumber: string,   // purchase_document_num
 *   departmentId: number,     // department_id
 *
 *   // Optional flag context (for flag review)
 *   flagId: number,
 *   flagIds: number[],
 *   categories: string[],
 *   hint: string,
 *
 *   // Source surface (for analytics)
 *   source: 'flag_review' | 'sankey' | 'vca' | 'po_detail' | 'triage' | 'chip' | 'other'
 * }
 */

export function ClassificationAgentProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState(null);
  const queryClient = useQueryClient();

  const openAgent = useCallback((ctx) => {
    if (!ctx?.itemId && !ctx?.flagId) {
      console.warn('ClassificationAgent: openAgent requires itemId or flagId');
      return;
    }
    setContext(ctx);
    setIsOpen(true);
  }, []);

  const closeAgent = useCallback(() => {
    setIsOpen(false);
    // Keep context briefly for animation, then clear
    setTimeout(() => setContext(null), 300);
  }, []);

  const onResolved = useCallback(() => {
    closeAgent();
    // Invalidate relevant queries
    queryClient.invalidateQueries({ queryKey: ['classification-flags'] });
    queryClient.invalidateQueries({ queryKey: ['procurement'] });
    queryClient.invalidateQueries({ queryKey: ['sankey'] });
  }, [closeAgent, queryClient]);

  const value = {
    isOpen,
    context,
    openAgent,
    closeAgent,
    onResolved,
  };

  return (
    <ClassificationAgentContext.Provider value={value}>
      {children}
    </ClassificationAgentContext.Provider>
  );
}

export default ClassificationAgentContext;
